import { useCallback, useEffect, useState } from "react";
import { useSearchParams } from "react-router-dom";
import axios from "axios";
import { toast } from "sonner";
import {
  Lock, Copy, Trash2, Loader2, Plus, CheckCircle2, Clock, Hash,
} from "lucide-react";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const PACK_PRESETS = [100, 250, 500, 1000, 2500];

const fmt = (n) => new Intl.NumberFormat("en-NZ").format(n || 0);

function relTime(iso) {
  if (!iso) return "—";
  try {
    const d = new Date(iso);
    return d.toLocaleString("en-NZ", { dateStyle: "medium", timeStyle: "short" });
  } catch {
    return iso;
  }
}

function StatusBadge({ status }) {
  const map = {
    active: { bg: "bg-[#2D4A22]/10", text: "text-[#2D4A22]", icon: CheckCircle2, label: "Active" },
    redeemed: { bg: "bg-[#1F5BC1]/10", text: "text-[#1F5BC1]", icon: Hash, label: "Redeemed" },
    expired: { bg: "bg-[#B83232]/10", text: "text-[#B83232]", icon: Clock, label: "Expired" },
  };
  const s = map[status] || map.active;
  const Icon = s.icon;
  return (
    <span className={`inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-bold uppercase tracking-wider ${s.bg} ${s.text}`}>
      <Icon className="w-3 h-3" /> {s.label}
    </span>
  );
}

function CodeRow({ row, token, onChange }) {
  const [busy, setBusy] = useState(false);
  const copy = () => {
    navigator.clipboard?.writeText(row.code);
    toast.success("Code copied");
  };
  const del = async () => {
    if (!confirm(`Delete ${row.code}? This can't be undone.`)) return;
    setBusy(true);
    try {
      await axios.delete(`${API}/admin/redeem-codes/${row.code}?token=${token}`);
      toast.success("Deleted");
      onChange();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Delete failed");
    } finally {
      setBusy(false);
    }
  };

  return (
    <tr className="border-b border-[#EAE6DC] hover:bg-[#F9F8F6]" data-testid={`code-row-${row.code}`}>
      <td className="py-3 px-3">
        <button
          onClick={copy}
          className="inline-flex items-center gap-1 font-mono text-sm font-bold hover:text-[#D96C32]"
          title="Copy code"
        >
          {row.code} <Copy className="w-3 h-3 opacity-60" />
        </button>
      </td>
      <td className="py-3 px-3 text-sm font-bold text-[#2D4A22]">{fmt(row.pack_size)}</td>
      <td className="py-3 px-3 text-sm">
        {row.phone_display ? (
          <span className="inline-flex items-center gap-1">
            <Lock className="w-3 h-3 text-[#4A4F4A]" /> {row.phone_display}
          </span>
        ) : (
          <span className="text-[#4A4F4A]/60 italic text-xs">open</span>
        )}
      </td>
      <td className="py-3 px-3"><StatusBadge status={row.status} /></td>
      <td className="py-3 px-3 text-xs text-[#4A4F4A]">{relTime(row.created_at)}</td>
      <td className="py-3 px-3 text-xs">
        {row.redeemed_at ? (
          <span>
            <span className="font-mono">{row.redeemed_by_phone_display || row.redeemed_by_phone}</span>
            <br />
            <span className="text-[#4A4F4A]/70">{relTime(row.redeemed_at)}</span>
          </span>
        ) : <span className="text-[#4A4F4A]/60">—</span>}
      </td>
      <td className="py-3 px-3 text-xs text-[#4A4F4A]">{row.note || "—"}</td>
      <td className="py-3 px-3 text-right">
        <button
          onClick={del}
          disabled={busy}
          data-testid={`code-delete-${row.code}`}
          className="text-[#B83232] hover:bg-[#B83232]/10 p-1.5 rounded transition-colors"
          title="Delete"
        >
          {busy ? <Loader2 className="w-4 h-4 animate-spin" /> : <Trash2 className="w-4 h-4" />}
        </button>
      </td>
    </tr>
  );
}

function CreateForm({ token, onCreated }) {
  const [packSize, setPackSize] = useState(250);
  const [customPack, setCustomPack] = useState("");
  const [phone, setPhone] = useState("");
  const [note, setNote] = useState("");
  const [quantity, setQuantity] = useState(1);
  const [loading, setLoading] = useState(false);

  const submit = async (e) => {
    e.preventDefault();
    const finalPack = packSize === "custom" ? parseInt(customPack || "0", 10) : packSize;
    if (!finalPack || finalPack <= 0) {
      toast.error("Pack size must be greater than 0");
      return;
    }
    setLoading(true);
    try {
      const { data } = await axios.post(
        `${API}/admin/redeem-codes?token=${token}`,
        {
          pack_size: finalPack,
          phone: phone.trim() || null,
          note: note.trim() || null,
          quantity: parseInt(quantity, 10),
        },
      );
      toast.success(`${data.count} code${data.count > 1 ? "s" : ""} created.`);
      setPhone(""); setNote(""); setQuantity(1);
      onCreated();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Create failed");
    } finally {
      setLoading(false);
    }
  };

  return (
    <form
      onSubmit={submit}
      data-testid="create-codes-form"
      className="bg-white border border-[#D5D1C8] rounded-md p-6 shadow-sm"
    >
      <h2 className="font-display font-bold text-xl text-[#1A1D1A] mb-5">
        Generate codes
      </h2>
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="sm:col-span-2">
          <label className="block text-xs font-bold tracking-[0.16em] uppercase text-[#4A4F4A] mb-2">
            Pack size (tokens)
          </label>
          <div className="flex flex-wrap gap-2">
            {PACK_PRESETS.map((p) => (
              <button
                key={p}
                type="button"
                onClick={() => setPackSize(p)}
                className={`px-3 py-1.5 rounded-full text-sm font-bold border transition-colors ${
                  packSize === p
                    ? "bg-[#2D4A22] border-[#2D4A22] text-white"
                    : "bg-white border-[#D5D1C8] text-[#1A1D1A] hover:border-[#2D4A22]"
                }`}
              >
                {fmt(p)}
              </button>
            ))}
            <button
              type="button"
              onClick={() => setPackSize("custom")}
              className={`px-3 py-1.5 rounded-full text-sm font-bold border transition-colors ${
                packSize === "custom"
                  ? "bg-[#D96C32] border-[#D96C32] text-white"
                  : "bg-white border-[#D5D1C8] text-[#1A1D1A] hover:border-[#D96C32]"
              }`}
            >
              Custom
            </button>
            {packSize === "custom" && (
              <input
                type="number"
                min="1"
                placeholder="e.g. 750"
                value={customPack}
                onChange={(e) => setCustomPack(e.target.value)}
                className="w-32 border border-[#D5D1C8] rounded-md px-3 py-1.5 text-sm focus:outline-none focus:ring-2 focus:ring-[#D96C32]"
              />
            )}
          </div>
        </div>

        <div>
          <label className="block text-xs font-bold tracking-[0.16em] uppercase text-[#4A4F4A] mb-2">
            Phone lock (optional)
          </label>
          <input
            type="tel"
            data-testid="create-phone"
            placeholder="021 123 4567 (leave empty for open code)"
            value={phone}
            onChange={(e) => setPhone(e.target.value)}
            className="w-full border border-[#D5D1C8] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2D4A22]"
          />
        </div>

        <div>
          <label className="block text-xs font-bold tracking-[0.16em] uppercase text-[#4A4F4A] mb-2">
            Quantity
          </label>
          <input
            type="number"
            min="1"
            max="100"
            data-testid="create-quantity"
            value={quantity}
            onChange={(e) => setQuantity(e.target.value)}
            className="w-full border border-[#D5D1C8] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2D4A22]"
          />
        </div>

        <div className="sm:col-span-2">
          <label className="block text-xs font-bold tracking-[0.16em] uppercase text-[#4A4F4A] mb-2">
            Note (optional)
          </label>
          <input
            type="text"
            data-testid="create-note"
            placeholder="e.g. Pete @ Morrison Farms — beta tester"
            value={note}
            onChange={(e) => setNote(e.target.value)}
            className="w-full border border-[#D5D1C8] rounded-md px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-[#2D4A22]"
          />
        </div>
      </div>

      <button
        type="submit"
        data-testid="create-submit"
        disabled={loading}
        className="mt-5 inline-flex items-center gap-2 bg-[#D96C32] hover:bg-[#B85A28] text-white font-bold px-6 py-3 rounded-md transition-colors disabled:opacity-60"
      >
        {loading ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
        {loading ? "Creating…" : "Create code(s)"}
      </button>
    </form>
  );
}

function SummaryCard({ label, value, sub, accent }) {
  return (
    <div className="bg-white border border-[#D5D1C8] rounded-md p-5 shadow-sm">
      <p className="text-xs font-bold uppercase tracking-wider text-[#4A4F4A]">{label}</p>
      <p className={`mt-2 font-display font-bold text-3xl ${accent || "text-[#1A1D1A]"}`}>{fmt(value)}</p>
      {sub && <p className="mt-1 text-xs text-[#4A4F4A]">{sub}</p>}
    </div>
  );
}

export default function AdminCodes() {
  const [params] = useSearchParams();
  const token = params.get("token") || "";
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [filter, setFilter] = useState("all");

  const load = useCallback(async () => {
    if (!token) return;
    setLoading(true);
    try {
      const res = await axios.get(`${API}/admin/redeem-codes?token=${token}`);
      setData(res.data);
      setError(null);
    } catch (e) {
      setError(e?.response?.data?.detail || "Failed to load");
    } finally {
      setLoading(false);
    }
  }, [token]);

  useEffect(() => { load(); }, [load]);

  if (!token) {
    return (
      <div className="min-h-screen bg-[#F2EFE7] flex items-center justify-center px-4">
        <div className="max-w-md text-center">
          <Lock className="w-10 h-10 text-[#4A4F4A] mx-auto" />
          <h1 className="mt-4 font-display font-bold text-2xl">Locked</h1>
          <p className="mt-2 text-[#4A4F4A]">Access this page with a token: <code className="font-mono">/admin/codes?token=…</code></p>
        </div>
      </div>
    );
  }

  if (loading && !data) {
    return (
      <div className="min-h-screen bg-[#F2EFE7] flex items-center justify-center">
        <Loader2 className="w-8 h-8 animate-spin text-[#2D4A22]" />
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-[#F2EFE7] flex items-center justify-center px-4">
        <div className="max-w-md text-center text-[#B83232]">
          <p>{error}</p>
        </div>
      </div>
    );
  }

  const codes = data?.codes || [];
  const filtered = filter === "all" ? codes : codes.filter((c) => c.status === filter);

  return (
    <div className="min-h-screen bg-[#F2EFE7] py-10 px-4 sm:px-8">
      <div className="max-w-6xl mx-auto">
        <div className="flex flex-wrap items-end justify-between gap-4 mb-8">
          <div>
            <a href="/" className="text-sm font-bold text-[#2D4A22] hover:underline">← arvo</a>
            <h1 className="mt-2 font-display font-bold text-3xl sm:text-4xl text-[#1A1D1A]">
              Redeem codes
            </h1>
            <p className="text-[#4A4F4A] mt-1">Issue, track and manage token codes for testers.</p>
          </div>
          <a
            href={`/stats?token=${token}`}
            className="text-sm font-bold text-[#4A4F4A] hover:text-[#2D4A22]"
          >
            View visitor stats →
          </a>
        </div>

        <div className="grid grid-cols-2 sm:grid-cols-5 gap-3 mb-8">
          <SummaryCard label="Total codes" value={data?.summary?.total_codes} />
          <SummaryCard label="Active" value={data?.summary?.active} accent="text-[#2D4A22]" />
          <SummaryCard label="Redeemed" value={data?.summary?.redeemed} accent="text-[#1F5BC1]" />
          <SummaryCard label="Tokens issued" value={data?.summary?.tokens_issued} sub="claimed by testers" accent="text-[#1F5BC1]" />
          <SummaryCard label="Outstanding" value={data?.summary?.tokens_outstanding} sub="pending claim" accent="text-[#D96C32]" />
        </div>

        <CreateForm token={token} onCreated={load} />

        <div className="mt-8 bg-white border border-[#D5D1C8] rounded-md shadow-sm overflow-hidden">
          <div className="flex flex-wrap items-center justify-between gap-3 px-6 py-4 border-b border-[#EAE6DC]">
            <h2 className="font-display font-bold text-xl text-[#1A1D1A]">All codes</h2>
            <div className="flex gap-2">
              {["all", "active", "redeemed", "expired"].map((f) => (
                <button
                  key={f}
                  onClick={() => setFilter(f)}
                  data-testid={`filter-${f}`}
                  className={`px-3 py-1 rounded-full text-xs font-bold uppercase tracking-wider transition-colors ${
                    filter === f
                      ? "bg-[#2D4A22] text-white"
                      : "bg-[#F9F8F6] text-[#4A4F4A] hover:bg-[#EAE6DC]"
                  }`}
                >
                  {f}
                </button>
              ))}
            </div>
          </div>

          <div className="overflow-x-auto">
            <table className="w-full text-left">
              <thead>
                <tr className="bg-[#F9F8F6] text-xs font-bold uppercase tracking-wider text-[#4A4F4A]">
                  <th className="py-3 px-3">Code</th>
                  <th className="py-3 px-3">Pack</th>
                  <th className="py-3 px-3">Locked to</th>
                  <th className="py-3 px-3">Status</th>
                  <th className="py-3 px-3">Created</th>
                  <th className="py-3 px-3">Redeemed by</th>
                  <th className="py-3 px-3">Note</th>
                  <th className="py-3 px-3 text-right">Actions</th>
                </tr>
              </thead>
              <tbody data-testid="codes-table-body">
                {filtered.length === 0 ? (
                  <tr>
                    <td colSpan={8} className="py-10 text-center text-[#4A4F4A]/70">
                      No codes here yet. Generate one above to get started.
                    </td>
                  </tr>
                ) : (
                  filtered.map((row) => (
                    <CodeRow key={row.code} row={row} token={token} onChange={load} />
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>
      </div>
    </div>
  );
}
